import { useEffect } from 'react'
import { useLocale } from '../../i18n/context'

type MCPhase = 'idle' | 'correct' | 'wrong'

interface Props {
  options: string[]          // exactly 4 shuffled terms
  correctAnswer: string      // to highlight after a wrong selection
  phase: MCPhase
  selectedOption: string | null
  onSelect: (option: string) => void
  disabled?: boolean
  isMobile?: boolean
}

/**
 * Multiple-choice exercise: four tappable option buttons.
 * - idle    — all options neutral, clickable
 * - correct — selected option turns green (✓)
 * - wrong   — selected option turns red (✗), correct option highlighted green
 *
 * Keyboard: press 1–4 to select an option when phase === 'idle'.
 */
export default function MultipleChoiceExercise({
  options,
  correctAnswer,
  phase,
  selectedOption,
  onSelect,
  disabled,
  isMobile,
}: Props) {
  const { tr } = useLocale()
  // Keyboard shortcuts: 1-4 select options
  useEffect(() => {
    if (phase !== 'idle' || disabled) return
    function handleKey(e: KeyboardEvent) {
      // Ignore when typing in an input elsewhere
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return
      const n = parseInt(e.key)
      if (n >= 1 && n <= options.length) {
        onSelect(options[n - 1])
      }
    }
    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [phase, options, onSelect, disabled])

  return (
    <div
      style={{
        width: '100%',
        maxWidth: 640,
        margin: '0 auto',
        display: 'flex',
        flexDirection: 'column',
        gap: isMobile ? 8 : 10,
      }}
    >
      <div
        style={{
          fontSize: 12,
          fontWeight: 600,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          color: 'var(--text-secondary)',
          fontFamily: 'var(--font-ui)',
          marginBottom: 2,
        }}
      >
        {tr.learn.chooseCorrectTerm}
      </div>

      {options.map((option, i) => {
        const isSelected = selectedOption === option
        const isCorrect = option === correctAnswer

        // Colour logic per phase
        let borderColor = 'var(--border)'
        let bgColor = 'var(--bg-surface)'
        let textColor = 'var(--text-primary)'
        let fontWeight: React.CSSProperties['fontWeight'] = 400

        if (phase === 'correct' && isSelected) {
          borderColor = 'var(--success)'
          bgColor = 'rgba(39, 174, 96, 0.08)'
          textColor = 'var(--success)'
          fontWeight = 600
        } else if (phase === 'wrong') {
          if (isSelected) {
            borderColor = 'var(--danger)'
            bgColor = 'var(--danger-soft)'
            textColor = 'var(--danger)'
            fontWeight = 600
          } else if (isCorrect) {
            borderColor = 'var(--success)'
            bgColor = 'rgba(39, 174, 96, 0.08)'
            textColor = 'var(--success)'
            fontWeight = 600
          }
        }

        const isClickable = phase === 'idle' && !disabled

        return (
          <button
            key={`${option}-${i}`}
            onClick={() => {
              if (isClickable) onSelect(option)
            }}
            style={{
              width: '100%',
              padding: isMobile ? '10px 14px' : '13px 18px',
              borderRadius: 12,
              border: `2px solid ${borderColor}`,
              backgroundColor: bgColor,
              color: textColor,
              fontSize: isMobile ? 16 : 18,
              fontFamily: 'var(--font-content)',
              fontWeight,
              cursor: isClickable ? 'pointer' : 'default',
              textAlign: 'left',
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              minHeight: isMobile ? 46 : 52,
              transition: 'border-color 0.18s, background-color 0.18s, color 0.18s, transform 0.1s',
              transform: 'scale(1)',
              outline: 'none',
              // Hover handled via onMouseEnter/Leave to avoid CSS specificity issues
            }}
            onMouseEnter={(e) => {
              if (!isClickable) return
              ;(e.currentTarget as HTMLButtonElement).style.transform = 'scale(1.01)'
              ;(e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--accent)'
            }}
            onMouseLeave={(e) => {
              if (!isClickable) return
              ;(e.currentTarget as HTMLButtonElement).style.transform = 'scale(1)'
              ;(e.currentTarget as HTMLButtonElement).style.borderColor = borderColor
            }}
          >
            {/* Keyboard shortcut badge — fades out once an answer is selected */}
            <span
              aria-hidden
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: 'var(--text-secondary)',
                background: 'var(--bg-secondary)',
                borderRadius: 4,
                padding: '2px 6px',
                fontFamily: 'var(--font-ui)',
                flexShrink: 0,
                minWidth: 20,
                textAlign: 'center',
                opacity: phase === 'idle' ? 0.7 : 0,
                transition: 'opacity 0.2s',
              }}
            >
              {i + 1}
            </span>

            <span style={{ flex: 1 }}>{option}</span>

            {/* Result icon on the right */}
            {/* lineHeight 1: at the option's own line height this 18 px glyph
                made the answered button ~4 px taller, which moved the card
                above it and re-measured the example sentence's font on answer. */}
            {phase !== 'idle' && isSelected && (
              <span style={{ fontSize: 18, lineHeight: 1, marginLeft: 'auto', flexShrink: 0 }}>
                {phase === 'correct' ? '✓' : '✗'}
              </span>
            )}
            {phase === 'wrong' && isCorrect && !isSelected && (
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: 'var(--success)',
                  marginLeft: 'auto',
                  flexShrink: 0,
                }}
              >
                {tr.learn.correctOption}
              </span>
            )}
          </button>
        )
      })}

      {/* Keyboard hint — desktop only. Always in DOM so its height contribution to
          Row 3 is constant across all phases; visibility:hidden hides it post-submit
          without removing it from flow (prevents options from shifting down). */}
      {!isMobile && (
        <p
          style={{
            margin: '4px 0 0',
            fontSize: 12,
            color: 'var(--text-secondary)',
            fontFamily: 'var(--font-ui)',
            textAlign: 'center',
            visibility: phase === 'idle' ? 'visible' : 'hidden',
          }}
        >
          {tr.learn.press14}
        </p>
      )}
    </div>
  )
}
